import {
  useLazyDataModel,
  string,
  shape,
  arrayOf,
  number,
  bool,
  customType,
  params
} from '@thd-nucleus/data-sources';

let INFLIGHT = false;

const RequestContext = shape({
  experience: shape({
    type: string()
  })
});

const CartInfoRequest = {
  filterItem: bool(),
  localization: shape({
    primaryStoreId: number()
  }),
  cartAttr: shape({
    essentialAccessories: bool()
  }),
  items: shape({
    delivery: arrayOf(
      shape({
        type: string(),
        itemId: string(),
        quantity: string(),
        location: string(),
        store: string(),
        storeSKU: string(),
        config: shape({
          type: string(),
          cardNumber: string(),
          name: string(),
          price: string(),
          availableAmount: string()
        })
      })
    )
  })
};

const dataModel = {
  addToCart: params({
    cartRequest: customType('CartInfoRequest').shape(CartInfoRequest).isRequired(),
    requestContext: customType('RequestContext').shape(RequestContext)
  })
    .mutation()
    .shape({
      cartId: string(),
      items: arrayOf(
        shape({
          id: string(),
          product: shape({
            itemId: string(),
            addons: arrayOf(
              shape({
                category: string(),
                configAttr: string()
              })
            ),
            identifiers: shape({
              productType: string(),
              cardNumber: string(),
              cardBalance: number(),
              projectedBalance: number()
            })
          })
        })
      ),
      totals: shape({
        total: number()
      })
    })
};

const handleUpdateCall = ({ mutator, variables, optimisticResponse = null }) => {
  return new Promise((resolve, reject) => {
    if (!INFLIGHT) {
      INFLIGHT = true;

      const mutationRequest = {
        variables,
        optimisticResponse,
        context: {
          headers: {
            credentials: 'include',
            mode: 'cors'
          }
        }
      };
      if (typeof window?.LIFE_CYCLE_EVENT_BUS !== 'undefined') {
        window.LIFE_CYCLE_EVENT_BUS.trigger('cart.dismissAlertsAndMessages');
      }

      mutator(mutationRequest)
        .then((response) => {
          if (response.errors && response.errors.length > 0) {
            INFLIGHT = false;
          }
          resolve(response.data);
        })
        .catch((error) => {
          INFLIGHT = false;
          reject(error);
        });
    }
  });
};

const useMutation = (mutationName, refetchedQueries) => {
  return useLazyDataModel(mutationName, {
    errorPolicy: 'all',
    dataModel: { [mutationName]: dataModel[mutationName] },
    update(cache, response) {
      const { data } = response || {};
      cache.modify({
        fields: {
          cartInfo(existingCartInfo) {
            if (data[mutationName]?.optimistic) {
              delete data[mutationName].optimistic;
            }
            return data[mutationName] || existingCartInfo;
          }
        }
      });
    },
    ...refetchedQueries
  });
};

const useCart = () => {
  const [cartMutation, atcResponse] = useMutation('addToCart');

  // mutation wrapper
  const addToCart = ({ cartRequest, requestContext, onComplete }) => {
    const variables = {
      cartRequest,
      requestContext
    };

    // returns promise
    return handleUpdateCall({
      mutator: cartMutation,
      variables,
      onComplete
    });
  };

  return [addToCart, atcResponse];
};

export { useCart };
